import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { formatAmount, money } from '../../lib/money';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Badge,
  Banner,
  Button,
  Card,
  ConfirmDialog,
  DescriptionList,
  EmptyState,
  Input,
  KpiTile,
  PageContainer,
  PageHeader,
  Spinner,
  Textarea,
  useToast,
} from '../../ui/components';
import { Icon } from '../../ui/icons';
import { useProperty } from '../../app/PropertyContext';
import { useT } from '../../i18n';
import { catalogApi } from '../catalog/catalogApi';
import { stockCountPath } from '../../lib/slug';
import { countsApi, countStatusMeta, countTypeLabel } from './countsApi';
import '../requisitions/requisitions.css';
import './counts.css';

const fmtQty = (n) => (n === null || n === undefined || n === '' ? '' : Number(n));

export default function CountDetailPage() {
  const { id: routeRef } = useParams();
  const navigate = useNavigate();
  const t = useT();
  const toast = useToast();
  const { hasPermission } = useProperty();
  const canCount = hasPermission('inventory.count');

  const [count, setCount] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [units, setUnits] = useState([]);
  const [locations, setLocations] = useState([]);

  const [edits, setEdits] = useState({}); // lineId -> { counted: string, notes: string }
  const [busy, setBusy] = useState(false);
  const [postOpen, setPostOpen] = useState(false);
  const [cancelOpen, setCancelOpen] = useState(false);
  const [cancelReason, setCancelReason] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      setCount(await countsApi.get(routeRef));
    } catch (err) {
      setError(err?.message || t('Count not found.'));
    } finally {
      setLoading(false);
    }
  }, [routeRef, t]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    let active = true;
    catalogApi.listUnits().then((u) => { if (active) setUnits(u || []); }).catch(() => {});
    catalogApi.listLocations().then((l) => { if (active) setLocations(l || []); }).catch(() => {});
    return () => { active = false; };
  }, []);

  useEffect(() => {
    if (!count?.id) return;
    const canonical = stockCountPath(count);
    if (`/counts/${routeRef}` !== canonical) navigate(canonical, { replace: true });
  }, [count, routeRef, navigate]);

  // Seed local edit state from the loaded count's lines.
  useEffect(() => {
    if (!count) return;
    const seed = {};
    (count.lines || []).forEach((l) => {
      seed[l.lineId] = {
        counted: l.countedQuantity === null || l.countedQuantity === undefined ? '' : String(l.countedQuantity),
        notes: l.notes || '',
      };
    });
    setEdits(seed);
  }, [count]);

  const uomCode = (uid) => units.find((u) => u.id === uid)?.code;
  const locationName = (lid) => locations.find((l) => l.id === lid)?.name;

  const isDraft = count?.status === 'Draft';
  const isPosted = count?.status === 'Posted';
  const isCancelled = count?.status === 'Cancelled';

  const setCounted = (lineId, value) =>
    setEdits((prev) => ({ ...prev, [lineId]: { ...prev[lineId], counted: value } }));

  const fillBlanks = () => {
    setEdits((prev) => {
      const next = { ...prev };
      (count.lines || []).forEach((l) => {
        if (!next[l.lineId] || next[l.lineId].counted === '') {
          next[l.lineId] = { ...next[l.lineId], counted: String(l.systemQuantity) };
        }
      });
      return next;
    });
  };

  const entriesPayload = useCallback(() => (count.lines || []).map((l) => {
    const e = edits[l.lineId] || {};
    const counted = e.counted === '' || e.counted === undefined ? null : Number(e.counted);
    return { lineId: l.lineId, countedQuantity: Number.isNaN(counted) ? null : counted, notes: e.notes || null };
  }), [count, edits]);

  const doSave = async () => {
    setBusy(true);
    try {
      const updated = await countsApi.saveCounts(count.id, { entries: entriesPayload(), notes: count.notes ?? null });
      setCount(updated);
      toast.success(t('Counts saved'));
    } catch (err) {
      toast.error(t('Could not save counts'), { description: err?.message });
    } finally {
      setBusy(false);
    }
  };

  const doPost = async () => {
    setBusy(true);
    try {
      // Persist any pending edits first so the posted variances match what's on screen.
      await countsApi.saveCounts(count.id, { entries: entriesPayload(), notes: count.notes ?? null });
      const posted = await countsApi.post(count.id);
      setPostOpen(false);
      setCount(posted);
      toast.success(t('Count {number} posted', { number: posted.number }));
    } catch (err) {
      toast.error(t('Could not post count'), { description: err?.message });
    } finally {
      setBusy(false);
    }
  };

  const doCancel = async () => {
    setBusy(true);
    try {
      const cancelled = await countsApi.cancel(count.id, cancelReason.trim() || null);
      setCancelOpen(false);
      setCancelReason('');
      setCount(cancelled);
      toast.success(isPosted ? t('Count cancelled and reversed') : t('Draft count discarded'));
    } catch (err) {
      toast.error(t('Could not cancel count'), { description: err?.message });
    } finally {
      setBusy(false);
    }
  };

  // Live variance preview (draft uses edits; posted/cancelled use stored figures).
  const summary = useMemo(() => {
    if (!count) return { counted: 0, gain: 0, loss: 0, net: 0 };
    let counted = 0; let gain = 0; let loss = 0;
    (count.lines || []).forEach((l) => {
      const raw = isDraft ? edits[l.lineId]?.counted : (l.countedQuantity ?? '');
      if (raw === '' || raw === null || raw === undefined) return;
      const c = Number(raw);
      if (Number.isNaN(c)) return;
      counted += 1;
      const variance = c - l.systemQuantity;
      const value = variance * Number(l.unitCost?.amount || 0);
      if (value >= 0) gain += value; else loss += value;
    });
    return { counted, gain, loss, net: gain + loss };
  }, [count, edits, isDraft]);

  if (loading) {
    return <PageContainer size="full"><div style={{ display: 'grid', placeItems: 'center', padding: 'var(--space-16)' }}><Spinner size={26} /></div></PageContainer>;
  }
  if (error || !count) {
    return (
      <PageContainer size="full">
        <EmptyState icon={<Icon.CircleAlert size={28} />} title={t("Couldn't load count")} description={error || t('Not found.')}
          action={<Button variant="secondary" onClick={() => navigate('/counts')}>{t('Back to counts')}</Button>} />
      </PageContainer>
    );
  }

  const statusMeta = countStatusMeta(count.status);
  const currency = count.currency || 'USD';

  const headerActions = canCount && !isCancelled ? (
    <div style={{ display: 'flex', gap: 'var(--space-2)' }}>
      {isDraft && (
        <>
          <Button variant="secondary" size="sm" loading={busy} leadingIcon={<Icon.Check size={14} />} onClick={doSave}>{t('Save')}</Button>
          <Button variant="primary" size="sm" leadingIcon={<Icon.CircleCheck size={14} />} onClick={() => setPostOpen(true)}>{t('Post count')}</Button>
        </>
      )}
      <Button variant="destructive" size="sm" leadingIcon={<Icon.Ban size={14} />} onClick={() => setCancelOpen(true)}>
        {isPosted ? t('Cancel & reverse') : t('Discard draft')}
      </Button>
    </div>
  ) : null;

  const overviewItems = [
    { term: t('Status'), description: <Badge tone={statusMeta.tone} dot>{t(statusMeta.label)}</Badge> },
    { term: t('Location'), description: locationName(count.locationId) || '—' },
    { term: t('Type'), description: t(countTypeLabel(count.countType)) },
    { term: t('Count date'), description: count.countDate ? new Date(count.countDate).toLocaleDateString() : '—' },
    { term: isPosted ? t('Posted by') : t('Created by'), description: (isPosted ? count.postedByName : count.createdByName) || '—' },
    { term: isPosted ? t('Posted at') : t('Created at'), description: new Date(isPosted ? count.postedAt : count.createdAt).toLocaleString() },
  ];

  const renderRow = (l) => {
    const raw = isDraft ? (edits[l.lineId]?.counted ?? '') : (l.countedQuantity ?? '');
    const hasCount = raw !== '' && raw !== null && raw !== undefined && !Number.isNaN(Number(raw));
    const variance = hasCount ? Number(raw) - l.systemQuantity : null;
    const varClass = variance === null ? 'cnt-var--zero' : variance > 0 ? 'cnt-var--pos' : variance < 0 ? 'cnt-var--neg' : 'cnt-var--zero';
    const uom = uomCode(l.uomId) ? ` ${uomCode(l.uomId)}` : '';
    return (
      <tr key={l.lineId}>
        <td>
          <div className="cnt-item__name">{l.description}</div>
          {l.sku && <div className="cnt-item__sub">{l.sku}</div>}
        </td>
        <td className="cnt-num">{fmtQty(l.systemQuantity)}{uom}</td>
        <td className="cnt-num">
          {isDraft ? (
            <Input
              className="cnt-count-input"
              type="number"
              step="any"
              value={raw}
              placeholder="—"
              onChange={(e) => setCounted(l.lineId, e.target.value)}
              disabled={!canCount}
            />
          ) : (
            hasCount ? `${Number(raw)}${uom}` : <span className="pp-cell-muted">{t('not counted')}</span>
          )}
        </td>
        <td className="cnt-num">
          {variance === null ? <span className="pp-cell-muted">—</span> : (
            <span className={`cnt-var ${varClass}`}>{variance > 0 ? '+' : ''}{variance}{uom}</span>
          )}
        </td>
        <td className="cnt-num">{money(l.unitCost)}</td>
        <td className="cnt-num">
          {variance === null ? <span className="pp-cell-muted">—</span> : (
            <span className={`cnt-var ${varClass}`}>{currency} {formatAmount(variance * Number(l.unitCost?.amount || 0))}</span>
          )}
        </td>
      </tr>
    );
  };

  return (
    <PageContainer size="full">
      <Button variant="ghost" size="sm" leadingIcon={<Icon.ArrowLeft size={15} />} onClick={() => navigate('/counts')} style={{ alignSelf: 'flex-start', marginBottom: 'var(--space-3)' }}>
        {t('Stock counts')}
      </Button>

      <PageHeader
        title={isDraft ? t('Draft count') : t('Count {number}', { number: count.number })}
        description={<span className="req-detail__number">{locationName(count.locationId) || ''} · {t(countTypeLabel(count.countType))}</span>}
        icon={<Icon.ClipboardList size={22} />}
        actions={headerActions}
      />

      {isDraft && (
        <div style={{ marginBottom: 'var(--space-4)' }}>
          <Banner tone="info">
            {t('This count is a draft — record the physical quantities, then post to apply the variances. On-hand balances are only changed when you post.')}
          </Banner>
        </div>
      )}

      {isCancelled && (
        <div style={{ marginBottom: 'var(--space-4)' }}>
          <Banner tone="danger">
            {t('This count was cancelled')}{count.cancelledByName ? ` ${t('by {name}', { name: count.cancelledByName })}` : ''}{count.cancelledAt ? ` ${t('on {date}', { date: new Date(count.cancelledAt).toLocaleString() })}` : ''}.{count.cancelledReason ? ` ${t('Reason: {reason}', { reason: count.cancelledReason })}` : ''}
          </Banner>
        </div>
      )}

      <div className="req-kpis">
        <KpiTile label={t('Items in scope')} value={count.lineCount} />
        <KpiTile label={t('Counted')} value={`${summary.counted}/${count.lineCount}`} />
        <KpiTile label={t('Net variance')} value={`${currency} ${formatAmount(summary.net)}`} />
        <KpiTile label={t('Shrinkage')} value={`${currency} ${formatAmount(summary.loss)}`} />
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-4)' }}>
        <Card>
          <Card.Body>
            <DescriptionList items={overviewItems} layout="stacked" columns={2} />
          </Card.Body>
        </Card>

        {count.notes && (
          <Card>
            <Card.Header><Card.Title>{t('Notes')}</Card.Title></Card.Header>
            <Card.Body>{count.notes}</Card.Body>
          </Card>
        )}

        <Card>
          <Card.Header>
            <div className="cnt-sheet__toolbar" style={{ marginBottom: 0, width: '100%' }}>
              <div>
                <Card.Title>{t('Count sheet')}</Card.Title>
                <div className="pp-cell-muted" style={{ fontSize: 'var(--text-sm)', marginTop: 2 }}>
                  {isDraft
                    ? t('Enter the physical count for each item. Leave blank to skip an item (no adjustment).')
                    : t('Variances posted to the inventory ledger; on-hand was reset to the counted figures.')}
                </div>
              </div>
              {isDraft && canCount && (
                <Button variant="ghost" size="sm" leadingIcon={<Icon.Copy size={14} />} onClick={fillBlanks}>
                  {t('Fill blanks with system qty')}
                </Button>
              )}
            </div>
          </Card.Header>
          <Card.Body>
            {(count.lines || []).length === 0 ? (
              <EmptyState icon={<Icon.Boxes size={26} />} title={t('No items in scope')} description={t('This count has no lines.')} />
            ) : (
              <div style={{ overflowX: 'auto' }}>
                <table className="cnt-sheet">
                  <thead>
                    <tr>
                      <th>{t('Item')}</th>
                      <th className="cnt-num">{t('System')}</th>
                      <th className="cnt-num">{t('Counted')}</th>
                      <th className="cnt-num">{t('Variance')}</th>
                      <th className="cnt-num">{t('Unit cost')}</th>
                      <th className="cnt-num">{t('Variance value')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(count.lines || []).map(renderRow)}
                  </tbody>
                </table>
              </div>
            )}
          </Card.Body>
        </Card>
      </div>

      <ConfirmDialog
        open={postOpen}
        title={t('Post count')}
        description={t('Posting writes each variance to the inventory ledger and resets on-hand to the counted figures. Uncounted items are left unchanged. You can still reverse it afterwards.')}
        confirmLabel={t('Post count')}
        tone="primary"
        loading={busy}
        onConfirm={doPost}
        onCancel={() => (busy ? null : setPostOpen(false))}
      />

      <ConfirmDialog
        open={cancelOpen}
        title={isPosted ? t('Cancel & reverse count') : t('Discard draft count')}
        description={isPosted
          ? t('This reverses the posted variance movements and restores the prior on-hand balances.')
          : t('This voids the draft count. Nothing has been committed, so there is nothing to reverse.')}
        confirmLabel={isPosted ? t('Cancel count') : t('Discard draft')}
        tone="danger"
        loading={busy}
        onConfirm={doCancel}
        onCancel={() => (busy ? null : setCancelOpen(false))}
      >
        <Textarea rows={2} value={cancelReason} onChange={(e) => setCancelReason(e.target.value)} placeholder={t('Reason (optional)')} />
      </ConfirmDialog>
    </PageContainer>
  );
}
